import { cloneElement, ReactElement } from 'react';

import {
  Box,
  ButtonBase,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { IcDocumentGray, IcNavigationPointer } from '@/assets/images';
import { SEVERITY_STATUS } from '@/constants';
import { TSeverity } from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';

import AddressNotesButton from './AddressNotesButton';

export const HeaderSection = ({
  title,
  subtitle,
  icon,
  severity,
  phoneNumber,
  primaryReporter,
  handleGetDirections,
  isDirectionsDisabled,
  hideNavigationButton = false,
  addressId,
  onAddressNotesExpand,
  showPreplanIcon,
  onAddressPreplanExpand,
}: {
  title: string;
  subtitle: string;
  icon?: ReactElement;
  severity?: TSeverity;
  phoneNumber?: string;
  primaryReporter?: string;
  handleGetDirections?: () => void;
  isDirectionsDisabled?: boolean;
  hideNavigationButton?: boolean;
  addressId?: string;
  onAddressNotesExpand?: () => void;
  showPreplanIcon?: boolean;
  onAddressPreplanExpand?: () => void;
}) => {
  const theme = useTheme();
  const { updateToast } = useToastStore();

  const handleDisabledClick = () => {
    updateToast({
      open: true,
      message: 'Please enable location services to get directions',
      type: ConfirmTypes.WARNING,
    });
  };

  const getColor = () => {
    switch (severity) {
      case TSeverity.HIGH:
        return SEVERITY_STATUS.HIGH;
      case TSeverity.MEDIUM:
        return SEVERITY_STATUS.MEDIUM;
      case TSeverity.LOW:
        return SEVERITY_STATUS.LOW;
      default:
        return colors.error.main;
    }
  };

  return (
    <>
      <Stack direction="row" gap={2} alignItems="center">
        <Typography variant="h4" fontWeight={600}>
          {title}
        </Typography>
        {icon &&
          cloneElement(icon, {
            height: 40,
            width: 40,
            style: {
              minWidth: 40,
            },
            color: getColor(),
          })}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h6"
          fontWeight={500}
          fontSize={21}
          marginY={2}
          color="#2974E2"
        >
          {subtitle}
        </Typography>
        <Stack direction="row" gap={1} alignItems="center">
          {showPreplanIcon ? (
            <ButtonBase
              onClick={onAddressPreplanExpand}
              style={{
                borderRadius: 999,
                backgroundColor: colors.grey[20],
                height: 36,
                padding: '0 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IcDocumentGray width={18} height={18} />
            </ButtonBase>
          ) : null}
          {addressId ? (
            <AddressNotesButton
              addressId={addressId}
              onExpand={onAddressNotesExpand || (() => {})}
            />
          ) : null}
          {hideNavigationButton ? null : (
            <Tooltip
              title={
                isDirectionsDisabled
                  ? 'Please enable location services to get directions'
                  : ''
              }
              placement="left"
              arrow
            >
              <ButtonBase
                aria-disabled={isDirectionsDisabled}
                onClick={
                  isDirectionsDisabled
                    ? handleDisabledClick
                    : handleGetDirections
                }
                style={{
                  borderRadius: 999,
                  backgroundColor: isDirectionsDisabled
                    ? colors.grey[40]
                    : '#2974E2',
                  opacity: isDirectionsDisabled ? 0.6 : 1,
                  cursor: isDirectionsDisabled ? 'not-allowed' : 'pointer',
                  height: 36,
                  padding: '0 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IcNavigationPointer fill="white" width={18} height={18} />
              </ButtonBase>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Stack gap={2} display="flex" direction="row" marginY={2}>
        <Box flex={1} display="flex" gap={1}>
          <Typography fontWeight={600} fontSize={16}>
            {primaryReporter}
          </Typography>
          {primaryReporter !== 'No reporter' ? (
            <Typography color={theme.palette.text.secondary}>
              (reporter)
            </Typography>
          ) : null}
        </Box>
        <Box flex={1}>
          <Typography fontWeight={600} fontSize={16}>
            {phoneNumber || 'No phone number'}
          </Typography>
        </Box>
      </Stack>
    </>
  );
};
